<template>
    <cic-popup
        id="edit_link_definition"
        :title="item.linkId ? $t('Edit LINK') : $t('New LINK')"
        :saveable="editing"
        @blur="$emit('blur')"
        @input="$emit('input')"
    >
        <ul class="item-form">
            <li class="form-label-first">
                {{ $t('Name') }}
            </li>
            <li>
                <cic-textbox
                    id="linkName"
                    :enabled="editing"
                    :value="item.name"
                    :maxlength="256"
                    @input="(val) => updateFieldValue('name', val)"
                />
            </li>
            <li class="form-label">
                {{ $t('UI Language') }}
            </li>
            <li>
                <CicSelect
                    id="language"
                    :enabled="editing"
                    :value="item.language"
                    :options="
                        languageOptions.map((option) => ({
                            label: option.language,
                            value: option.languageCode,
                        }))
                    "
                    @input="(val) => updateFieldValue('language', val)"
                />
            </li>
            <li class="form-label">{{ $t('Assigned Queues') }}</li>
            <li>
                <CicSelect
                    id="link_queues"
                    :enabled="editing"
                    :value="item.queueIds"
                    :options="queues.map((queue) => ({ value: queue.queueId, label: queue.name }))"
                    multiple
                    @input="(val) => updateFieldValue('queueIds', val)"
                />
            </li>
            <li class="form-label">{{ $t('Assigned Agent Groups') }}</li>
            <li>
                <CicSelect
                    id="link_agGroups"
                    :enabled="editing"
                    :value="item.agentGroupIds"
                    :options="groups.map((group) => ({ value: group.id, label: group.name }))"
                    multiple
                    @input="(val) => updateFieldValue('agentGroupIds', val)"
                />
            </li>
            <li class="form-label">{{ $t('Message Template') }}</li>
            <li>
                <cic-textarea
                    id="messageTemplate"
                    :enabled="editing"
                    :value="item.messageTemplate"
                    :maxlength="4000"
                    @input="(val) => updateFieldValue('messageTemplate', val)"
                />
            </li>

            <li class="inline-form-item">
                <cic-checkbox
                    id="checkboxCopyPaste"
                    :enabled="editing"
                    :value="item.copyPasteOptionSelected"
                    @input="(val) => updateFieldValue('copyPasteOptionSelected', val)"
                />
                <span class="checkbox-label">{{ $t('Allow agent to edit template / copy link') }}</span>
            </li>

            <li class="form-label">
                {{ $t('Link Types') }}
            </li>
            <li class="inline-form-item">
                <cic-checkbox
                    id="checkboxVideo"
                    :enabled="editing"
                    :value="item.linkTypes.includes(1)"
                    @click="updateLinkTypes(1)"
                />
                <span class="checkbox-label">{{ $t('Video') }}</span>
                <cic-checkbox
                    id="checkboxFileshare"
                    :enabled="editing"
                    :value="item.linkTypes.includes(2)"
                    @click="updateLinkTypes(2)"
                />
                <span class="checkbox-label">{{ $t('Fileshare') }}</span>
                <cic-checkbox
                    id="checkboxLocation"
                    :enabled="editing"
                    :value="item.linkTypes.includes(4)"
                    @click="updateLinkTypes(4)"
                />
                <span class="checkbox-label">{{ $t('Location') }}</span>
            </li>

            <template v-if="$store.hasRight('UNSOLICITED_LINK')">
                <li class="form-label">
                    {{ $t('Enable Unsolicited LINK') }}
                </li>
                <li class="inline-form-item">
                    <cic-checkbox
                        id="checkboxUnsolicitedLink"
                        :enabled="editing"
                        :value="item.unsolicitedLink"
                        @input="(val) => updateFieldValue('unsolicitedLink', val)"
                    />
                </li>
            </template>

            <li class="form-label">
                {{ $t('Send Options') }}
            </li>
            <li class="inline-form-item">
                <cic-checkbox
                    id="checkboxSMS"
                    :enabled="editing && smsClis.length !== 0"
                    :value="smsClis.length !== 0 ? item.smsOptionSelected : false"
                    @input="(val) => updateFieldValue('smsOptionSelected', val)"
                />
                <span class="checkbox-label">{{ $t('SMS') }}</span>
            </li>
            <li v-if="item.smsOptionSelected && smsClis.length !== 0" style="padding-bottom: 6px">
                <CicSelect
                    id="fromCLI"
                    :enabled="editing"
                    :value="item.cli"
                    :options="
                        smsClis.map((smsCli) => ({
                            value: smsCli.szCLINumber,
                            label: smsCli.szCLIName,
                        }))
                    "
                    @input="(val) => updateFieldValue('cli', val)"
                />
            </li>
            <li class="inline-form-item">
                <cic-checkbox
                    id="checkboxEmail"
                    :enabled="editing && emailAddresses.length !== 0"
                    :value="emailAddresses.length !== 0 ? item.emailOptionSelected : false"
                    @input="(val) => updateFieldValue('emailOptionSelected', val)"
                />
                <span class="checkbox-label">{{ $t('Email') }}</span>
            </li>
            <li v-if="item.emailOptionSelected && emailAddresses.length !== 0">
                <CicSelect
                    id="fromEmailAddress"
                    :enabled="editing"
                    :value="item.emailAddress"
                    :options="
                        emailAddresses.map((emailAddress) => ({
                            value: emailAddress.szReplyToAddress,
                            label: emailAddress.szEmailAddressName,
                        }))
                    "
                    @input="(val) => updateFieldValue('emailAddress', val)"
                />
            </li>
        </ul>
    </cic-popup>
</template>

<script>
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import CicTextarea from '@/components/inputs/CicTextarea';
    import CicPopup from '@/components/CicPopup';

    export default {
        name: 'EditLinkDefinition',
        components: {
            CicSelect,
            CicCheckbox,
            CicTextbox,
            CicTextarea,
            CicPopup,
        },
        props: {
            item: {
                type: Object,
                required: true,
            },
            queues: {
                type: Array,
                required: true,
            },
            groups: {
                type: Array,
                required: true,
            },
            emailAddresses: {
                type: Array,
                required: true,
            },
            smsClis: {
                type: Array,
                required: true,
            },
            languageOptions: {
                type: Array,
                required: true,
            },
        },
        emits: ['blur', 'change', 'input'],
        data() {
            return {
                editing: false,
            };
        },
        created() {
            this.editing = !this.item.linkId || this.$store.hasRight('EDIT_LINK');
        },
        methods: {
            updateLinkTypes(value) {
                const types = [...this.item.linkTypes];
                if (!types.includes(value)) {
                    types.push(value);
                } else {
                    types.splice(types.indexOf(value), 1);
                }

                this.updateFieldValue('linkTypes', types);
            },

            updateFieldValue(field, value) {
                this.$emit('change', field, value);
            },
        },
    };
</script>
